<template>
  <v-menu
    v-model="menuIsVisible"
    content-class='w-header-picker w-menu'
    :min-width='minWidth'
    :close-on-content-click="false"
    offset-y
  >
    <template #activator="{ on }">
      <v-tooltip bottom>
        <template #activator="{ on: onTooltip }">
          <w-table-layout-button v-on="{ ...on, ...onTooltip }" />
        </template>
        <span>{{ $t('Common.chooseColumns') }}</span>
      </v-tooltip>
    </template>

    <v-list min-width='auto'>
      <v-list-item v-if='allowColumnModeSwitch'>
        <v-list-item-action class='mr-4'>
          <w-switch
            :value='truncateData'
            @input='$emit("update:truncate-data", $event)'
          />
        </v-list-item-action>

        <v-list-item-content>
          <v-list-item-title>{{ $t('Components.sentences.shortenLengthOfData') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item
        v-for="(header, index) in internalHeaders"
        :key="index"
        class="d-flex flex-wrap flex-row"
      >
        <v-list-item-action>
          <w-checkbox
            :input-value="header.isVisible"
            @change="_changeHeaderVisibility(header.value)"
          />
        </v-list-item-action>

        <v-list-item-content
          class='mr-8'
          style='min-width: 7rem; overflow: unset;text-overflow: unset;'
        >
          <v-list-item-title>{{ header.text }}</v-list-item-title>
          <v-list-item-subtitle v-if='truncateOptions[header.value]'>
            {{ $t(`TruncateOptions.${truncateOptions[header.value].unit}`) }}
          </v-list-item-subtitle>
        </v-list-item-content>

        <v-list-item-content
          v-if="truncateOptions[header.value]"
          style="min-width: 7rem"
          class="mr-8"
        >
          <w-number-text-field
            v-if='truncateOptions[header.value]'
            :disabled='!truncateData'
            :value='truncateOptions[header.value].length'
            :text-align='truncateData ? "start" : "end"'
            @input='$emit("update:truncate-options", { key: header.value, value: $event })'
          >
            <template
              v-if='truncateData'
              #append
            >
              <w-refresh-button @click.stop='$emit("update:truncate-options", { key: header.value, value: undefined })'>
                <template #tooltip>
                  {{ $t('Components.sentences.createDefaultValue') }}
                </template>
              </w-refresh-button>
            </template>
          </w-number-text-field>
        </v-list-item-content>

        <v-list-item-action class='list-icon-button-action'>
          <w-up-button
            v-show='index > 0'
            @click='_swapHeaderPos(header.value, false)'
          />
        </v-list-item-action>

        <v-list-item-action class='list-icon-button-action'>
          <w-down-button
            v-show='index !== orderdHeaders.length - 1'
            @click='_swapHeaderPos(header.value, true)'
          />
        </v-list-item-action>
      </v-list-item>

      <v-list-item
        v-if="$vuetify.breakpoint.xsOnly"
        class="w-menu-close-wrapper"
      >
        <w-btn
          outlined
          block
          @click="menuIsVisible = false"
        >
          {{ $t('Common.close') }} 
        </w-btn>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

export default {
  model: { prop: 'headers' },
  props: {
    // data
    headers: { type: Array, required: true },
    orderdHeaders: { type: Array, required: true },
    notVisibleHeaders: { type: Array, required: true },
    truncateData: { type: Boolean, required: true },
    truncateOptions: { type: Object, required: true },
    // flags
    allowColumnModeSwitch: { type: Boolean, required: true },
  },
  data() {
    return {
      menuIsVisible: false,
    };
  },
  computed: {
    internalHeaders() {
      if (this.orderdHeaders == undefined || this.notVisibleHeaders == undefined) { return [] }
      return this._addVisibleStateToOrderdHeaders(this._internalOrderdHeaders);
    },
    _internalOrderdHeaders() {
      return this._mapOrderdHeadersToOrderdHeaderWithFullData(this.orderdHeaders, this.headers);
    },
    minWidth() {
      if (this.$vuetify.breakpoint.smAndDown) { return "100vw" }
      return undefined;
    }
  },
  methods: {
    _addVisibleStateToOrderdHeaders(orderdHeaders) {
      return orderdHeaders.map((_header) => {
        return { ..._header, isVisible: _isHeaderVisible(_header?.value, this.notVisibleHeaders) }
      })
    },
    _mapOrderdHeadersToOrderdHeaderWithFullData(headerOrder, headers) {
      return headerOrder.map((headerIdentifier) => {
        return _getHeaderDataOf(headerIdentifier, headers);
      });
    },
    _changeHeaderVisibility(headerIdentifier) {
      let newNotVisibleHeaders = cloneDeep(this.notVisibleHeaders);

      const indexOfHeader = _getIndexOf(headerIdentifier, newNotVisibleHeaders);
      if (indexOfHeader < 0) {
        newNotVisibleHeaders = _addHeaderToNotVisibleHeaders(newNotVisibleHeaders, headerIdentifier);
      } else {
        newNotVisibleHeaders = _removeHeaderOfNotVisibleHeaders(newNotVisibleHeaders, indexOfHeader);
      }

      this.$emit('update:not-visible-headers', newNotVisibleHeaders);
    },
    _swapHeaderPos(headerIdentifier, up) {
      let newHeaderOrder = cloneDeep(this.orderdHeaders);
      const indexOfheaderIdentifier = _getIndexOf(headerIdentifier, newHeaderOrder);

      if (up === true) {
        newHeaderOrder = _swapPosUp(newHeaderOrder, headerIdentifier, indexOfheaderIdentifier)
      } else {
        newHeaderOrder = _swapPosDown(newHeaderOrder, headerIdentifier, indexOfheaderIdentifier)
      }

      this.$emit('update:orderd-headers', newHeaderOrder);
    },
  }
};

const _isHeaderVisible = (headerIdentifier, notVisibleHeaders) => {
  if (notVisibleHeaders === undefined) { return true }

  const index = notVisibleHeaders.findIndex((_headerIdentifier) => _headerIdentifier === headerIdentifier);
  return index < 0;
}

const _getHeaderDataOf = (headerIdentifier, headers) => {
  return headers.find((_header) => _header.value === headerIdentifier);
}

const _getIndexOf = (headerIdentifier, inArray) => {
  return inArray.findIndex((_headerIdentifier) => _headerIdentifier === headerIdentifier);
}

const _addHeaderToNotVisibleHeaders = (notVisibleHeaders, headerIdentifier) => {
  notVisibleHeaders.push(headerIdentifier);
  return notVisibleHeaders;
}

const _removeHeaderOfNotVisibleHeaders = (notVisibleHeaders, index) => {
  notVisibleHeaders.splice(index, 1);
  return notVisibleHeaders;
}

const _swapPosUp = (headerOrder, headerIdentifier, index) => {
  headerOrder[index] = headerOrder[index + 1];
  headerOrder[index + 1] = headerIdentifier;
  return headerOrder;
}

const _swapPosDown = (headerOrder, headerIdentifier, index) => {
  headerOrder[index] = headerOrder[index- 1];
  headerOrder[index - 1] = headerIdentifier;
  return headerOrder;
}
</script>

<style lang='scss' scoped>
.w-header-picker {
  @include header-menu
}
</style>
